// src/components/Admin/ExecutiveSummaryModal.js
// Executive Summary modal with date range selection, Word export, and email sharing

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Chip,
    Divider,
    Alert,
    TextField,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Link
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    Share as ShareIcon,
    ExpandMore as ExpandMoreIcon,
    QuestionAnswer as QAIcon,
    Campaign as SignalIcon,
    Description as MemoIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, ExternalHyperlink } from 'docx';
import { toast } from 'react-toastify';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import ShareModal from '../ShareModal';

const ExecutiveSummaryModal = ({ open, onClose, client, user }) => {
    // Date range state (default: last 30 days)
    const getDefaultDates = () => {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        
        return {
            from: thirtyDaysAgo.toISOString().split('T')[0],
            to: today.toISOString().split('T')[0]
        };
    };

    const [dateFrom, setDateFrom] = useState(getDefaultDates().from);
    const [dateTo, setDateTo] = useState(getDefaultDates().to);
    const [isLoading, setIsLoading] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        topics: true,
        signals: true,
        recommendations: true
    });

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            const defaults = getDefaultDates();
            setDateFrom(defaults.from);
            setDateTo(defaults.to);
            setSummaryData(null);
            setExpandedSections({ topics: true, signals: true, recommendations: true });
        }
    }, [open]);

    // Generate executive summary
    const handleGenerate = async () => {
        if (!dateFrom || !dateTo) {
            toast.error('Please select both start and end dates.');
            return;
        }

        if (new Date(dateFrom) > new Date(dateTo)) {
        toast.error('Start date must be before end date.');
        return;
    }

    if (!client?.id) {
        toast.error('Client information is not available yet.');
        return;
    }

    setIsLoading(true);
        setSummaryData(null);

        try {
            const generateExecSummary = httpsCallable(
                functions,
                'generateExecutiveSummary',
                { timeout: 300000 }
            );

            const result = await generateExecSummary({
                clientId: client.id,
                dateFrom,
                dateTo
            });

            if (result.data.success) {
                setSummaryData(result.data.executiveSummary);
                toast.success(result.data.message);
            } else {
                throw new Error(result.data.error || 'Failed to generate summary');
            }
        } catch (error) {
            console.error('Executive Summary generation failed:', error);
            toast.error(error.message || 'Failed to generate Executive Summary');
        } finally {
            setIsLoading(false);
        }
    };

    // Navigate to thread in feed
    const handleThreadClick = (itemId) => {
        if (!itemId) return;
        const encodedId = encodeURIComponent(itemId);
        window.open(`/firm-portal?tab=activity&search=${encodedId}`, '_blank');
    };

    // Toggle section expansion
    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Format date range for display
    const formatDateRange = () => {
        const from = new Date(dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const to = new Date(dateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${from} – ${to}`;
    };

    // Export to Word document
    const handleExportToWord = async () => {
        if (!summaryData) {
            toast.error('No summary data to export.');
            return;
        }

        try {
            const { metadata, topicsDiscussed, signalsSent, recommendationsGenerated } = summaryData;

            // Build document sections
            const children = [
                // Title
                new Paragraph({
                    text: `Executive Summary: ${metadata.clientName}`,
                    heading: HeadingLevel.TITLE
                }),
                // Metadata header
                new Paragraph({
                    children: [
                        new TextRun({ text: 'Period: ', bold: true }),
                        new TextRun({ text: formatDateRange() }),
                        new TextRun({ text: '  |  ' }),
                        new TextRun({ text: 'Generated: ', bold: true }),
                        new TextRun({ text: new Date(metadata.generatedAt).toLocaleDateString() })
                    ],
                    spacing: { after: 100 }
                }),
                // Activity counts
                new Paragraph({
                    children: [
                        new TextRun({ text: 'Activity: ', bold: true }),
                        new TextRun({ text: `${metadata.counts.questions} Q&A Sessions • ${metadata.counts.signals} SavvaiSignals • ${metadata.counts.decisionMemos} Decision Memos` })
                    ],
                    spacing: { after: 100 }
                }),
                // Key themes
                new Paragraph({
                    children: [
                        new TextRun({ text: 'Key Themes: ', bold: true }),
                        new TextRun({ text: topicsDiscussed.keyThemes.length > 0 ? topicsDiscussed.keyThemes.join(', ') : 'None identified' })
                    ],
                    spacing: { after: 300 }
                }),
                new Paragraph({ text: '────────────────────────────────────────', spacing: { after: 200 } }),

                // TOPICS DISCUSSED
                new Paragraph({
                    text: 'TOPICS DISCUSSED',
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 200 }
                })
            ];

            // Add questions
            if (topicsDiscussed.questions.length > 0) {
                topicsDiscussed.questions.forEach(q => {
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({ text: '• ', bold: true }),
                                new TextRun({ text: q.text }),
                                new TextRun({ text: ` (${q.date})`, italics: true })
                            ],
                            spacing: { after: 50 }
                        })
                    );
                    if (q.themes && q.themes.length > 0) {
                        children.push(
                            new Paragraph({
                                children: [
                                    new TextRun({ text: '  Themes: ', bold: true }),
                                    new TextRun({ text: q.themes.join(', ') })
                                ],
                                spacing: { after: 100 },
                                indent: { left: 360 }
                            })
                        );
                    }
                });
            } else {
                children.push(new Paragraph({ text: 'No Q&A sessions in this period.', spacing: { after: 200 } }));
            }

            children.push(new Paragraph({ text: '────────────────────────────────────────', spacing: { before: 200, after: 200 } }));

            // SIGNALS SENT
            children.push(
                new Paragraph({
                    text: 'SAVVAISIGNALS SENT',
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 200 }
                })
            );

            if (signalsSent.signals.length > 0) {
                signalsSent.signals.forEach(s => {
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({ text: '• ', bold: true }),
                                new TextRun({ text: s.title, bold: true }),
                                new TextRun({ text: ` (${s.date})`, italics: true })
                            ],
                            spacing: { after: 50 }
                        })
                    );
                    // Add highlights if available
                    const highlights = recommendationsGenerated.signalHighlights.find(h => h.signalId === s.id);
                    if (highlights?.highlights?.length > 0) {
                        highlights.highlights.forEach(h => {
                            children.push(
                                new Paragraph({
                                    children: [new TextRun({ text: `  – ${h}` })],
                                    spacing: { after: 30 },
                                    indent: { left: 360 }
                                })
                            );
                        });
                    }
                    children.push(new Paragraph({ text: '', spacing: { after: 100 } }));
                });
            } else {
                children.push(new Paragraph({ text: 'No SavvaiSignals in this period.', spacing: { after: 200 } }));
            }

            children.push(new Paragraph({ text: '────────────────────────────────────────', spacing: { before: 200, after: 200 } }));

            // RECOMMENDATIONS GENERATED
            children.push(
                new Paragraph({
                    text: 'RECOMMENDATIONS GENERATED',
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 200 }
                })
            );

            // Q&A Recommendations
            if (recommendationsGenerated.qaRecommendations.length > 0) {
                children.push(
                    new Paragraph({
                        text: 'From Q&A Sessions:',
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 150, after: 100 }
                    })
                );
                recommendationsGenerated.qaRecommendations.forEach(rec => {
                    if (rec.action || rec.impact) {
                        children.push(
                            new Paragraph({
                                children: [
                                    new TextRun({ text: 'Action: ', bold: true }),
                                    new TextRun({ text: rec.action || 'No action extracted' })
                                ],
                                spacing: { after: 30 }
                            })
                        );
                        if (rec.impact) {
                            children.push(
                                new Paragraph({
                                    children: [
                                        new TextRun({ text: 'Impact: ', bold: true }),
                                        new TextRun({ text: rec.impact })
                                    ],
                                    spacing: { after: 50 },
                                    indent: { left: 360 }
                                })
                            );
                        }
                        children.push(
                            new Paragraph({
                                children: [
                                    new TextRun({ text: `${rec.date} — ${rec.questionText}`, italics: true })
                                ],
                                spacing: { after: 150 },
                                indent: { left: 360 }
                            })
                        );
                    }
                });
            }

            // Decision Memos
            if (recommendationsGenerated.decisionMemos.length > 0) {
                children.push(
                    new Paragraph({
                        text: 'Decision Memos:',
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 200, after: 100 }
                    })
                );
                recommendationsGenerated.decisionMemos.forEach(memo => {
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({ text: '• ', bold: true }),
                                new TextRun({ text: memo.title, bold: true }),
                                new TextRun({ text: ` (${memo.date})`, italics: true })
                            ],
                            spacing: { after: 50 }
                        })
                    );
                    if (memo.summary) {
                        children.push(
                            new Paragraph({
                                children: [new TextRun({ text: `  ${memo.summary}` })],
                                spacing: { after: 100 },
                                indent: { left: 360 }
                            })
                        );
                    }
                });
            }

            // Footer
            children.push(
                new Paragraph({ text: '────────────────────────────────────────', spacing: { before: 300, after: 100 } }),
                new Paragraph({
                    children: [
                        new TextRun({ text: 'Generated by SavvaiEdge™', italics: true }),
                        new TextRun({ text: ` on ${new Date().toLocaleString()}`, italics: true })
                    ],
                    spacing: { after: 100 }
                })
            );

            // Create document
            const doc = new Document({
                sections: [{ children }]
            });

            // Generate and download
            const blob = await Packer.toBlob(doc);
            const filename = `Executive_Summary_${metadata.clientName.replace(/[^a-zA-Z0-9]/g, '_')}_${dateFrom}_to_${dateTo}.docx`;
            
            const link = document.createElement('a');
            const url = window.URL.createObjectURL(blob);
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Executive Summary exported to Word!');
        } catch (error) {
            console.error('Word export failed:', error);
            toast.error('Failed to export Word document.');
        }
    };

    // Handle email share
    const handleShare = async (sharePayload) => {
        if (!summaryData) return { success: false, error: 'No summary to share' };

        const payloadForBackend = {
            recipientEmail: sharePayload.recipientEmail,
            personalMessage: sharePayload.personalMessage,
            summaryData: {
                clientName: summaryData.metadata.clientName,
                dateRange: summaryData.metadata.dateRange,
                generatedAt: summaryData.metadata.generatedAt,
                counts: summaryData.metadata.counts,
                keyThemes: summaryData.topicsDiscussed.keyThemes,
                topicsDiscussed: summaryData.topicsDiscussed,
                signalsSent: summaryData.signalsSent,
                recommendationsGenerated: summaryData.recommendationsGenerated
            }
        };

        try {
            const shareReport = httpsCallable(functions, 'shareExecutiveSummaryByEmail');
            await shareReport(payloadForBackend);
            toast.success('Executive Summary shared successfully!');
            return { success: true };
        } catch (error) {
            console.error('Share failed:', error);
            toast.error(`Failed to share summary: ${error.message}`);
            return { success: false, error: error.message };
        }
    };

    // Render the summary content
    const renderSummaryContent = () => {
        if (!summaryData) return null;

        const { metadata, profileFreshness, topicsDiscussed, signalsSent, recommendationsGenerated } = summaryData;

        return (
            <Box>
                {/* Profile Freshness Warning */}
                {profileFreshness.message && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        ⚠️ {profileFreshness.message}
                    </Alert>
                )}

                {/* Metadata Header */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        {metadata.clientName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Period:</strong> {formatDateRange()} &nbsp;|&nbsp; 
                        <strong>Activity:</strong> {metadata.counts.questions} Q&A • {metadata.counts.signals} Signals • {metadata.counts.decisionMemos} Memos
                    </Typography>
                    {topicsDiscussed.keyThemes.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" component="span" fontWeight={600}>
                                Key Themes:{' '}
                            </Typography>
                            {topicsDiscussed.keyThemes.map((theme, idx) => (
                                <Chip
                                    key={idx}
                                    label={theme}
                                    size="small"
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Topics Discussed Section */}
                <Accordion 
                    expanded={expandedSections.topics} 
                    onChange={() => toggleSection('topics')}
                    sx={{ mb: 1 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <QAIcon color="primary" />
                            <Typography fontWeight={600}>
                                Topics Discussed ({topicsDiscussed.questions.length})
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {topicsDiscussed.questions.length > 0 ? (
                            <Stack spacing={1.5}>
                                {topicsDiscussed.questions.map((q) => (
                                    <Box key={q.id} sx={{ pl: 1, borderLeft: '3px solid #e0e0e0' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">
                                                {q.text}
                                            </Typography>
                                            <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleThreadClick(q.id)}
                                                                    title="View in Feed"
                                                                >
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {q.date}
                                            </Typography>
                                            {q.themes?.map((theme, idx) => (
                                                <Chip key={idx} label={theme} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                            ))}
                                            {q.hasFollowUps && (
                                                <Chip label={`${q.followUpCount} follow-up${q.followUpCount > 1 ? 's' : ''}`} size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No Q&A sessions in this period.
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* Signals Sent Section */}
                <Accordion 
                    expanded={expandedSections.signals} 
                    onChange={() => toggleSection('signals')}
                    sx={{ mb: 1 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SignalIcon color="secondary" />
                            <Typography fontWeight={600}>
                                SavvaiSignals Sent ({signalsSent.signals.length})
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {signalsSent.signals.length > 0 ? (
                            <Stack spacing={2}>
                                {signalsSent.signals.map((s) => {
                                    const highlights = recommendationsGenerated.signalHighlights.find(h => h.signalId === s.id);
                                    return (
                                        <Box key={s.id} sx={{ pl: 1, borderLeft: '3px solid #e0e0e0' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {s.title}
                                                </Typography>
                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleThreadClick(s.id)}
                                                                    title="View in Feed"
                                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {s.date}
                                            </Typography>
                                            {highlights?.highlights?.length > 0 && (
                                                <Box sx={{ mt: 1, pl: 1 }}>
                                                    <Typography variant="caption" fontWeight={600}>Key Highlights:</Typography>
                                                    <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                                                        {highlights.highlights.map((h, idx) => (
                                                            <li key={idx}>
                                                                <Typography variant="caption">{h}</Typography>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No SavvaiSignals in this period.
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* Recommendations Generated Section */}
                <Accordion 
                    expanded={expandedSections.recommendations} 
                    onChange={() => toggleSection('recommendations')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MemoIcon color="success" />
                            <Typography fontWeight={600}>
                                Recommendations Generated
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {/* Q&A Recommendations */}
                        {recommendationsGenerated.qaRecommendations.some(r => r.action || r.impact) && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    From Q&A Sessions:
                                </Typography>
                                <Stack spacing={1.5}>
                                    {recommendationsGenerated.qaRecommendations.filter(r => r.action || r.impact).map((rec) => (
                                        <Box key={rec.questionId} sx={{ pl: 1, borderLeft: '3px solid #4caf50' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                                <Box>
                                                    {rec.action && (
                                                        <Typography variant="body2">
                                                            <strong>Action:</strong> {rec.action}
                                                        </Typography>
                                                    )}
                                                    {rec.impact && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Impact:</strong> {rec.impact}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleThreadClick(rec.questionId)}
                                                                    title="View in Feed"
                                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {rec.date} — {rec.questionText}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* Decision Memos */}
                        {recommendationsGenerated.decisionMemos.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Decision Memos:
                                </Typography>
                                <Stack spacing={1.5}>
                                    {recommendationsGenerated.decisionMemos.map((memo) => (
                                        <Box key={memo.memoId} sx={{ pl: 1, borderLeft: '3px solid #2196f3' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {memo.title}
                                                </Typography>
                                                <IconButton 
                                                                    size="small" 
                                                                    // Use memo.parentId to filter the feed to the parent thread
                                                                    onClick={() => handleThreadClick(memo.parentId)}
                                                                    title="View in Feed"
                                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            {memo.summary && (
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    {memo.summary}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                {memo.date} — from {memo.parentType === 'question' ? 'Q&A' : 'Signal'}: {memo.parentTitle}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* No recommendations message */}
                        {!recommendationsGenerated.qaRecommendations.some(r => r.action || r.impact) && 
                         recommendationsGenerated.decisionMemos.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                No recommendations extracted from this period's activity.
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* Generation info */}
                <Divider sx={{ mt: 2, mb: 1 }} />
                <Typography variant="caption" color="text.secondary">
    Generated: {new Date(metadata.generatedAt).toLocaleString()} by {user?.displayName || user?.email || 'Unknown'}
</Typography>
            </Box>
        );
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '70vh' }
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                            {isLoading 
                                ? 'Generating Executive Summary...' 
                                : summaryData 
                                    ? `Executive Summary: ${summaryData.metadata.clientName}`
                                    : `Executive Summary: ${client?.name || client?.officialCompanyName || ''}`
                            }
                        </Typography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {/* Date Range Selection (shown before generation) */}
                    {!summaryData && !isLoading && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Select a date range to generate an executive summary of all Q&A sessions, 
                                SavvaiSignals, and Decision Memos for this client.
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <TextField
                                    label="From"
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ width: 180 }}
                                />
                                <TextField
                                    label="To"
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ width: 180 }}
                                />
                                <Button
    variant="contained"
    onClick={handleGenerate}
    disabled={!dateFrom || !dateTo || !client?.id}
>
    Generate Summary
</Button>
                            </Stack>
                        </Box>
                    )}

                    {/* Loading State */}
                    {isLoading && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="300px"
                        >
                            <CircularProgress size={48} sx={{ mb: 2 }} />
                            <Typography variant="body1" color="text.secondary">
                                Analyzing Q&A sessions, Signals, and Decision Memos...
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                This may take 15-30 seconds.
                            </Typography>
                        </Box>
                    )}

                    {/* Summary Content */}
                    {summaryData && !isLoading && renderSummaryContent()}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
    {summaryData && !isLoading && (
        <>
            <Button
                onClick={() => {
                    setSummaryData(null);
                    const defaults = getDefaultDates();
                    setDateFrom(defaults.from);
                    setDateTo(defaults.to);
                }}
                color="inherit"
            >
                New Summary
            </Button>
            <Box sx={{ flexGrow: 1 }} />
        </>
    )}
    <Button onClick={onClose} color="inherit">
        Close
    </Button>
    {summaryData && !isLoading && (
        <>
            <Button
                onClick={() => setIsShareModalOpen(true)}
                startIcon={<ShareIcon />}
            >
                Share
            </Button>
            <Button
                onClick={handleExportToWord}
                variant="contained"
                startIcon={<DownloadIcon />}
            >
                Export to Word
            </Button>
        </>
    )}
</DialogActions>
            </Dialog>

            {/* Share Modal */}
            {isShareModalOpen && summaryData && (
                <ShareModal
                    open={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    item={summaryData}
                    itemType="executive_summary"
                    onShare={handleShare}
                />
            )}
        </>
    );
};

export default ExecutiveSummaryModal;